import React, { useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import {
  addDays,
  compareAsc,
  differenceInDays,
  format,
  isSameDay,
  parseISO,
} from "date-fns";
import { toZonedTime } from "date-fns-tz";
import Select, {
  components,
  OptionProps,
  SingleValue,
  SingleValueProps,
} from "react-select";
import { useTimeZoneContext } from "../../../../lib/context/TimeZone/TimeZoneContext";
import { selectStyles } from "../../../../lib/utils/selectStyles";
import { Button } from "../../Button/Button";
import { Icon } from "../../Icon/Icon";
import { forReservationDate } from "../../../Bookings/lib/datePickerHelper";
import { defineDatesPicker } from "../../../Bookings/lib/defineDatesPicker";
import { MAX_BOOKING_WINDOW_DAYS } from "../../../../lib/utils/constants";

import "./DaySelectorInput.scss";

export type InputType = { label: string; value: string; dates?: string[] };

type CustomDateVariant = "room" | "desk";

type DaySelectorOption = InputType & {
  isCustomDateOption?: boolean;
  isCustomDateSelected?: boolean;
};

interface Props {
  onChange: (value: InputType | null) => void;
  value?: InputType;
  customDateVariant?: CustomDateVariant;
  maxSelectedDates?: number;
}

const CUSTOM_DATE_OPTION_VALUE = "custom-date";

const customDateOptionLabels: Record<CustomDateVariant, string> = {
  room: "Choose another date",
  desk: "Another date or multi-day booking",
};

const multiDatesFromValue = (value?: InputType): Date[] =>
  value?.dates?.map((date) => parseISO(date)) ?? [];

const limitedMultiDatesFromValue = (
  value?: InputType,
  maxSelectedDates?: number
): Date[] => {
  const dates = multiDatesFromValue(value);

  return maxSelectedDates ? dates.slice(0, maxSelectedDates) : dates;
};

const DaySelectorOptionComponent = (
  props: OptionProps<DaySelectorOption, false>
): JSX.Element => (
  <components.Option
    {...props}
    className={[
      "DaySelectorInput__option",
      props.data.isCustomDateOption ? "DaySelectorInput__option--custom" : "",
      props.data.isCustomDateSelected ? "DaySelectorInput__option--active" : "",
    ]
      .filter(Boolean)
      .join(" ")}
  >
    <span>{props.data.label}</span>
    {props.data.isCustomDateSelected && <Icon icon="check" />}
  </components.Option>
);

const DaySelectorSingleValueComponent = (
  props: SingleValueProps<DaySelectorOption, false>
): JSX.Element => {
  const extraDatesCount = (props.data.dates?.length ?? 0) - 1;

  return (
    <components.SingleValue {...props}>
      {props.data.label}
      {extraDatesCount > 0 && (
        <span className="DaySelectorInput__badge">+{extraDatesCount}</span>
      )}
    </components.SingleValue>
  );
};

export const DaySelectorInput = ({
  onChange,
  value,
  customDateVariant,
  maxSelectedDates,
}: Props) => {
  const { timeZone } = useTimeZoneContext();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    value?.value ? parseISO(value.value) : null
  );
  const [selectedDates, setSelectedDates] = useState<Date[]>(
    limitedMultiDatesFromValue(value, maxSelectedDates)
  );

  const { datePickOptions } = defineDatesPicker(timeZone);

  const minDate = useMemo(
    () => parseISO(format(toZonedTime(new Date(), timeZone), "yyyy-MM-dd")),
    [timeZone]
  );
  const maxDate = useMemo(
    () => addDays(minDate, MAX_BOOKING_WINDOW_DAYS),
    [minDate]
  );

  const options = useMemo<DaySelectorOption[]>(() => {
    if (!customDateVariant) {
      return datePickOptions;
    }

    const isMultiDateSelection =
      customDateVariant === "desk" && (value?.dates?.length ?? 0) > 1;

    const isBeyondPresetWindow =
      !!value?.value &&
      differenceInDays(parseISO(value.value), minDate) >=
        datePickOptions.length;

    return [
      ...datePickOptions,
      {
        label: customDateOptionLabels[customDateVariant],
        value: CUSTOM_DATE_OPTION_VALUE,
        isCustomDateOption: true,
        isCustomDateSelected: isMultiDateSelection || isBeyondPresetWindow,
      },
    ];
  }, [customDateVariant, datePickOptions, value, minDate]);

  const resetDraftFromValue = () => {
    const currentSelectedDate = value?.value ? parseISO(value.value) : null;

    setSelectedDate(currentSelectedDate);

    if (customDateVariant === "desk") {
      setSelectedDates(limitedMultiDatesFromValue(value, maxSelectedDates));
    }
  };

  const handleChange = (option: SingleValue<DaySelectorOption>) => {
    if (option?.isCustomDateOption) {
      resetDraftFromValue();
      setIsMenuOpen(false);
      setIsCalendarOpen(true);

      return;
    }

    onChange(option);
  };

  const handleCancel = () => {
    resetDraftFromValue();
    setIsCalendarOpen(false);
    setIsMenuOpen(true);
  };

  const commitDates = (dates: Date[]) => {
    const sortedDates = [...dates].sort(compareAsc);
    const dateToApply = sortedDates[0];

    if (!dateToApply) {
      return;
    }

    onChange({
      label: forReservationDate(dateToApply, timeZone),
      value: format(dateToApply, "yyyy-MM-dd"),
      ...(customDateVariant === "desk"
        ? { dates: sortedDates.map((date) => format(date, "yyyy-MM-dd")) }
        : {}),
    });
  };

  const handleClearDates = () => {
    setSelectedDate(minDate);
    setSelectedDates([minDate]);
    commitDates([minDate]);
  };

  const handleApply = () => {
    commitDates(
      customDateVariant === "desk"
        ? selectedDates
        : selectedDate
          ? [selectedDate]
          : []
    );
    setIsCalendarOpen(false);
  };

  const handleMultiDateChange = (date: Date | null) => {
    if (!date) {
      return;
    }

    setSelectedDates((dates) => {
      const isAlreadySelected = dates.some((selectedDate) =>
        isSameDay(selectedDate, date)
      );

      if (isAlreadySelected) {
        return dates.filter((selectedDate) => !isSameDay(selectedDate, date));
      }

      if (maxSelectedDates && dates.length >= maxSelectedDates) {
        return dates;
      }

      return [...dates, date];
    });
  };

  const canSelectMultiDate = (date: Date) =>
    !maxSelectedDates ||
    selectedDates.length < maxSelectedDates ||
    selectedDates.some((selectedDate) => isSameDay(selectedDate, date));

  return (
    <div className="DaySelectorInput">
      <Select<DaySelectorOption, false>
        name="day"
        options={options}
        theme={selectStyles}
        minMenuHeight={55}
        isSearchable={false}
        className="SearchWorkplaceFrom__select"
        classNamePrefix="DaySelectorInputSelect"
        components={{
          Option: DaySelectorOptionComponent,
          SingleValue: DaySelectorSingleValueComponent,
        }}
        onChange={handleChange}
        menuIsOpen={isCalendarOpen ? false : isMenuOpen}
        onMenuOpen={() => setIsMenuOpen(true)}
        onMenuClose={() => setIsMenuOpen(false)}
        value={value}
      />

      {isCalendarOpen && <div className="DaySelectorInput__backdrop" />}

      <div
        className={[
          "DaySelectorInput__calendar",
          isCalendarOpen ? "DaySelectorInput__calendar--open" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-hidden={!isCalendarOpen}
      >
        <div className="DaySelectorInput__calendarHeader">
          <Button
            type="button"
            color="simple"
            icon="close-icon"
            iconOnly
            tooltip="Close"
            className="Button--icon-plain Button--icon-action DaySelectorInput__close"
            onClick={handleCancel}
          />
        </div>

        <div className="DaySelectorInput__calendarBody">
          {customDateVariant === "desk" ? (
            <DatePicker
              inline
              selected={undefined}
              onChange={(date: Date | null) => handleMultiDateChange(date)}
              shouldCloseOnSelect={false}
              disabledKeyboardNavigation
              minDate={minDate}
              maxDate={maxDate}
              filterDate={canSelectMultiDate}
              highlightDates={[
                {
                  "DaySelectorInput__day--multiSelected": selectedDates,
                },
              ]}
            />
          ) : (
            <DatePicker
              inline
              selected={selectedDate}
              onChange={(date: Date | null) => setSelectedDate(date)}
              minDate={minDate}
              maxDate={maxDate}
            />
          )}
        </div>

        <div className="DaySelectorInput__calendarFooter">
          <Button
            type="button"
            title="Clear"
            color="outline-primary"
            className="DaySelectorInput__footerButton"
            onClick={handleClearDates}
          />
          <Button
            type="button"
            title="Apply"
            color="primary"
            className="DaySelectorInput__footerButton"
            onClick={handleApply}
            disabled={
              customDateVariant === "desk"
                ? selectedDates.length === 0
                : !selectedDate
            }
          />
        </div>
      </div>
    </div>
  );
};
